import * as React from 'react';
import {Component, deps, inject, observer} from '../../lib/component';
import {classes, style} from 'typestyle';
import {
  center,
  centerCenter,
  centerJustified,
  horizontal,
  horizontallySpaced,
  vertical,
  verticallySpaced
} from 'csstips';
import {important, percent, px, viewWidth} from 'csx';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import gql from 'graphql-tag';
import {Loading} from '../loading/loading';
import {AddRemoveCompat} from '../compat/add-remove-compat';
import {BrandSponsoredLabelCompat} from '../compat/brand-sponsored-label-compat';
import {FavouriteButtonCompat} from '../compat/favourite-button-compat';
import {PlayButtonCompat} from '../compat/play-button-compat';
import {PaginationSelect} from '../pagination/select';
import {BrowseState} from '../../modules/page/browse/model';
import {ReleaseTracks} from '../release-tracks';
import {colorWhite} from '../../theme/color';
import {PaginationMeta} from '../../types';
import {zUnderLayer} from '../../theme/z';
import {TrackSchema} from '../../types/schema';
import {PageHeaderCarousel, PageHeaderCarouselModel} from '../page-header-carousel';
import extractUniqueArtists from '../../lib/extractUniqueArtists';
import {Carousel} from '../carousel';
import {PlaylistTile} from '../playlist-tile';
import {TagTile} from '../tag-tile';
import {mediaDesktop, mediaMobileOnly, mediaTablet} from '../../theme/media';
import {TagTileGrid, Tile} from '../tile-grid';
import MainWrapper from '../main-wrapper';
import maybe from '../../lib/maybe';

export interface BrowseResultsProps extends BrowseState {
  hidePagination?: boolean;
  pathGenerate?: (page: number) => string;
  pathRoot?: string;
}

export type BrowseResultsDataType = {
  item: {
    name: string;
    slug: string;
    description: string;
    images: {
      identity: string
    };
    tracks: {
      pagination: {
        total: number
        totalPages: number
        from: number
        size: number
        page: number
      }
      results: TrackSchema[]
    }
  }
  related: {
    results: Array<{
      name: string;
      slug: string;
      images: {
        identity: string;
      }
    }>
  }
};

type DataProps = {
  loading: boolean;
  error?: any;
}

type MaybeData = BrowseResultsDataType & DataProps | null;
type CombinedProps = BrowseResultsProps & InjectedGraphQLProps<BrowseResultsDataType>;


@inject(deps) @observer
export class BrowseResults extends Component<CombinedProps> {
  render() {
    const { data, hidePagination, pathGenerate, pathRoot } = this.props;
    const { isMobile } = this.props.model.ui;
    const { type, makeRelatedPath, relatedLabel } = this.props.model.page.browse;
    const { goToPage } = this.props.controller.page.browse;
    const { preloadImage, preloadWaveformSvg, preloadContent } = this.props.controller.image;
    const { handleClickLink } = this.props.controller.ui;

    const hasHits = data && data.item && data.item.tracks && data.item.tracks.results && data.item.tracks.results;
    const hasRelated = data && data.related && data.related.results && data.related.results;
    const description = data && data.item && data.item.description
      ? data.item.description
      : null;

    return (
      <MainWrapper className={BrowseResults.styles.container}>
        <PageHeaderCarousel
          className={BrowseResults.styles.pageCarousel}
          model={this.getHeroModel(data)}
          onClickLink={handleClickLink}
          preloadContent={preloadContent}
        />
        {data.error && (
          <div className={BrowseResults.styles.loadingOverlay}>
            <h2>Hmm. Something ain't right here...</h2>
            <p>We've been notified and are on the case, please try again later!</p>
          </div>
        )}
        <div className={BrowseResults.styles.split}>
          {hasHits && (
            <div className={BrowseResults.styles.tracks}>
              <h3>Tracks</h3>
              <ReleaseTracks
                AddRemove={AddRemoveCompat}
                BrandSponsoredLabel={BrandSponsoredLabelCompat}
                Favourite={FavouriteButtonCompat}
                PlayButton={PlayButtonCompat}
                isMobile={isMobile}
                onClickLink={handleClickLink}
                preloadWaveform={preloadWaveformSvg}
                model={this.releaseTracksModel(data.item.tracks.results)}
              />
              {!hidePagination && hasHits && (
                <div className={BrowseResults.styles.pagination}>
                  <PaginationSelect
                    pathRoot={pathRoot}
                    pagination={this.paginationMeta(data.item.tracks.pagination)}
                    isMobile={isMobile}
                    loadPage={goToPage}
                    pathGenerate={pathGenerate}
                  />
                </div>
              )}
            </div>
          )}
          {(description || hasRelated) && (
            <div>
              {description && (
                <div>
                  <h3 className={BrowseResults.styles.sideHeading}>About</h3>
                  <p className={BrowseResults.styles.description}>{description}</p>
                </div>
              )}
              {hasRelated && (
                <div>
                  <h3 className={BrowseResults.styles.sideHeading}>{relatedLabel}</h3>
                  <TagTileGrid
                    className={BrowseResults.styles.tileGrid}
                    tiles={BrowseResults.mapTiles(data.related.results)}
                    perRowTablet={2}
                    perRowDesktop={3}
                    loading={data.loading}
                    loadingTiles={12}
                    loadingSize={24}
                    renderTile={(itemClass, tile, index) => this.renderTile(tile, {
                      key: tile.slug + index,
                      href: makeRelatedPath(this.getPath(type, tile)),
                      onClickLink: handleClickLink,
                      preload: preloadImage,
                      className: classes(itemClass, BrowseResults.styles.tileGridItem)
                    })}
                  />
                  <Carousel className={BrowseResults.styles.carousel}>
                    {BrowseResults.mapTiles(data.related.results).map((related, index) => this.renderTile(related, {
                      key: related.slug + index,
                      href: makeRelatedPath(this.getPath(type, related)),
                      onClickLink: handleClickLink,
                      preload: preloadImage,
                      className: BrowseResults.styles.tile
                    }))}
                  </Carousel>
                </div>
              )}
            </div>
          )}
        </div>
      </MainWrapper>
    )
  }

  renderTile = (tile: Tile<any>, commonProps) => {
    const {isCollection} = this.props.model.page.browse;

    switch (true) {
      case (isCollection): return (
        <PlaylistTile playlist={tile} {...commonProps}/>
      );

      default: return (
        <TagTile tag={tile} {...commonProps}/>
      );
    }
  };

  componentWillMount() {
    this.updateSEO();
  }

  componentDidUpdate(lastProps: CombinedProps) {
    this.updateSEO();
  }

  updateSEO = () => {
    const type = maybe(() => this.props.model.page.browse.type, null);
    const name = maybe(() => this.props.data.item.name, null);

    if (!type || !name)
      return;

    const mappedType = type.toLowerCase();

    this.props.controller.ui.setLastBreadcrumbLabel(name);
    this.props.controller.ui.setSEO(`/browse/${mappedType}/slug`, {[mappedType]: name});
  };

  releaseTracksModel = (hits: any[]) => ({
    loading: false,
    tracks: hits,
  });

  getPath = (type: string, tile: Tile<any>) => {
    if (type === 'labels') {
      return `${tile.model.artist.slug}/release/${tile.slug}`
    }

    return tile.slug;
  };

  getHeroModel = (data: MaybeData): PageHeaderCarouselModel => ({
    loading: !data || data.loading,
    artists: data && data.item && data.item.tracks && data.item.tracks.results
      ? extractUniqueArtists(data.item.tracks.results).slice(0, 5).map(a => ({
        name: a.name,
        slug: a.slug,
        image: a.images ? a.images.identity : null
      }))
      : [],
    title: data && data.item && data.item.name
      ? data.item.name
      : data.error
        ? 'Oops 🤦'
        : 'Loading...'
  });

  paginationMeta = (meta: any): PaginationMeta => ({
    count: meta.total,
    current_page: meta.page + 1, // 0-index graphql, 1-index PaginationSelect
    per_page: meta.size,
    total: meta.total,
    total_pages: meta.totalPages
  });

  static mapTiles = (results: BrowseResultsDataType['related']['results']): Tile<any>[] => {
    return results.map(r => ({
      ...r,
      model: r
    }));
  };

  static styles = {
    container: style({
      position: 'relative',
    }),
    split: style(
      mediaMobileOnly({
        ...vertical,
        ...verticallySpaced(16),
        ...center,
        $nest: {
          '&>div': {
            width: percent(100),
          },
          '&>div:first-child': {
            padding: px(16)
          }
        }
      }),
      mediaTablet({
        ...horizontal,
        ...horizontallySpaced(16),
        ...centerJustified,
        padding: px(16),
        $nest: {
          '&>div:first-child': {
            flex: 2,
          },
          '&>div:last-child': {
            flex: 1,
          }
        }
      })
    ),
    description: style(
      {
        whiteSpace: 'pre-wrap',
        margin: '1rem 0 2rem'
      },
      mediaMobileOnly({
        padding: '0 1rem'
      })
    ),
    pageCarousel: style(),
    loadingOverlay: style({
      ...centerCenter,
      ...vertical,
      zIndex: zUnderLayer,
      padding: '16px 80px',
      maxWidth: px(420),
      minHeight: px(320),
      background: colorWhite.fade(0.9).toString()
    }),
    tracks: style({}),
    pagination: style({
      ...centerCenter,
    }),
    sideHeading: style(
      mediaMobileOnly({ padding: '0 16px' })
    ),
    carousel: style(
      {
        minHeight: px(190),
        display: 'none',
      },
      mediaMobileOnly({ height: viewWidth(52), display: 'block' }),
    ),
    tile: style(
      { flexShrink: 0, height: 'auto !important', minWidth: px(190), $nest: { '& *': { fontSize: px(20) }} },
      mediaMobileOnly({ width: viewWidth(52) })
    ),
    tileGrid: style(
      {
        width: percent(100),
        display: important('none')
      },
      mediaTablet({
        display: important('flex')
      })
    ),
    tileGridItem: style(
      mediaTablet({
        $nest: {
          '& div > *': {
            fontSize: important(viewWidth(100 * 8/420).toString())
          }
        }
      }),
      mediaDesktop({
        $nest: {
          '& div > div': {
            fontSize: important(viewWidth(100 * 4/420).toString())
          }
        }
      })
    )
  }
}

export const CollectionBrowseResults = graphql(gql`
  query (
    $options: GetCollectionInput!
    $related: BrowseQueryInput!
    $pagination: PaginationInput!
  ) {
    related: browseCollection(options: $related) {
      results {
        name
        slug
        images {
          identity
        }
      }
    }
    item: getCollection(options: $options) {
      name
      slug
      images {
        identity
      }
      tracks(pagination: $pagination) {
        pagination {
          total
          totalPages
          from
          size
          page
        }
        results {
          identity
          title
          slug
          duration
          created_at
          is_charting
          is_featured
          is_new_release
          brand_sponsored
          branded_content
          audio {
            identity
          }
          images {
            identity
          }
          artists {
            images {
              identity
            }
            name
            slug
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: BrowseResultsProps) => ({
    variables,
    // fetchPolicy: 'network-only'
  })
})(BrowseResults);

export const TagBrowseResults = graphql(gql`
  query (
  $options: GetTagInput!
  $related: BrowseQueryInput!
  $pagination: PaginationInput!
  ) {
    related: browseTag(options: $related) {
      results {
        name
        slug
        images {
          identity
        }
      }
    }
    item: getTag(options: $options) {
      name
      slug
      images {
        identity
      }
      tracks(pagination: $pagination) {
        pagination {
          total
          totalPages
          from
          size
          page
        }
        results {
          identity
          title
          slug
          duration
          created_at
          is_charting
          is_featured
          is_new_release
          brand_sponsored
          branded_content
          audio {
            identity
          }
          images {
            identity
          }
          artists {
            images {
              identity
            }
            name
            slug
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: BrowseResultsProps) => ({
    variables,
    // fetchPolicy: 'network-only'
  })
})(BrowseResults);

export const RightsholderBrowseResults = graphql(gql`
  query (
  $options: GetRightsholderInput!
  $related: BrowseQueryInput!
  $pagination: PaginationInput!
  ) {
    related: browseRelease(options: $related) {
      results {
        name: title
        slug
        images {
          identity
        }
        artist {
          slug
        }
      }
    }
    item: getRightsholder(options: $options) {
      name
      slug
      description
      images {
        identity
      }
      tracks(pagination: $pagination) {
        pagination {
          total
          totalPages
          from
          size
          page
        }
        results {
          identity
          title
          slug
          duration
          created_at
          is_charting
          is_featured
          is_new_release
          brand_sponsored
          branded_content
          audio {
            identity
          }
          images {
            identity
          }
          artists {
            images {
              identity
            }
            name
            slug
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: BrowseResultsProps) => ({
    variables,
    // fetchPolicy: 'network-only'
  })
})(BrowseResults);

export const FilterBrowseResults = graphql(gql`
  query (
  $options: GetFilterInput!
  $related: BrowseQueryInput!
  $pagination: PaginationInput!
  ) {
    related: browseFilter(options: $related) {
      results {
        name: title
        slug
        images {
          identity
        }
      }
    }
    item: getFilter(options: $options) {
      name: title
      slug
      images {
        identity
      }
      tracks(pagination: $pagination) {
        pagination {
          total
          totalPages
          from
          size
          page
        }
        results {
          identity
          title
          slug
          duration
          created_at
          is_charting
          is_featured
          is_new_release
          brand_sponsored
          branded_content
          audio {
            identity
          }
          images {
            identity
          }
          artists {
            images {
              identity
            }
            name
            slug
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: BrowseResultsProps) => ({
    variables,
    // fetchPolicy: 'network-only'
  })
})(BrowseResults);